import { api, LightningElement, wire } from "lwc";
import { getEmitter } from "@devforce/trailhead-client-events";
import { logger } from "@devforce/trailhead-client-logger";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { getAssetsUrl } from "common/helpers";
import * as locales from "./locales";
import emptyStampArt from "./assets/empty_stamp.png";

import type {
  StampEarnedType,
  StampLockedType,
} from "@devforce/trailhead-client-gql";
import type { State as LoadingState } from "tds/contentCollectionLoadingState";

export type CollectionMode = "locked" | "unlocked";
export type CollectionVariant = "default" | "simple";

export default class StampCollection extends LightningElement {
  @api variant: CollectionVariant = "default";
  @api mode: CollectionMode = "locked";
  @api stamps: (StampEarnedType | StampLockedType)[] | null = null;
  @api totalStamps: number = 0;
  @api state: LoadingState = "loading";
  private loadingMoreStamps = false;
  private switchingViews = false;

  connectedCallback(): void {
    getEmitter().on("custom", (e) => {
      switch (e.name) {
        case "th-stamp-collection:load-more-completed":
          this.loadingMoreStamps = false;
          break;
        case "th-stamp-collection:initial-load-completed":
          this.switchingViews = false;
          break;
        default:
      }
    });
  }

  disconnectedCallback(): void {
    getEmitter().off("custom");
  }

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  private get labelOptions() {
    return {
      "earned.titleSingular": {
        count: `${this.totalStamps}`,
      },
      "earned.titlePlural": {
        count: `${this.totalStamps}`,
      },
      "locked.titleSingular": {
        count: `${this.totalStamps}`,
      },
      "locked.titlePlural": {
        count: `${this.totalStamps}`,
      },
      "misc.description": {
        salesforcePlusLink: `<a href='https://www.salesforce.com/plus' class="link" target="_blank">Salesforce+</a>`,
      },
    };
  }

  /**
   * Event Handlers
   */

  private handleModeChange(e: CustomEvent) {
    let eventName = "";
    switch (e.detail as CollectionMode) {
      case "locked":
        eventName = "tds-stamp-collection:load-locked";
        break;
      case "unlocked":
        eventName = "tds-stamp-collection:load-earned";
        break;
      default:
        logger.warn(
          `tds-stamp-collection[handleModeChange]: unknown mode: ${e.detail}`
        );
        return;
    }

    // Emit an event via the shared message bus
    getEmitter().emit("custom", {
      name: eventName,
      payload: null,
    });
    this.switchingViews = true;
    this.mode = e.detail as CollectionMode;
  }

  private handleCta() {
    this.loadingMoreStamps = true;
    // Emit an event via the shared message bus
    getEmitter().emit("custom", {
      name: "tds-stamp-collection:load-more",
      payload: null,
    });
  }

  /**
   * Template-Only Methods
   * ---------------------
   * These methods are used in the template only and are not called from the
   * component's JavaScript code.
   * ---------------------
   * */

  private get getCtaLabel() {
    if (!this.stamps) return null;

    return this.totalStamps > this.stamps.length
      ? `${this.labels["misc.showMore"]}`
      : null;
  }

  private get getDescription() {
    return `${this.labels["misc.description"]}`;
  }

  private get getComboboxAriaLabel() {
    return `${this.labels["filter.ariaLabel"]}`;
  }

  private get getCollectionTitle() {
    switch (this.mode) {
      case "locked":
        return this.totalStamps === 1
          ? this.labels["locked.titleSingular"]
          : this.labels["locked.titlePlural"];
      case "unlocked":
      default:
        return this.totalStamps === 1
          ? this.labels["earned.titleSingular"]
          : this.labels["earned.titlePlural"];
    }
  }

  private get zeroStamps() {
    return this.totalStamps === 0 && this.switchingViews == false;
  }

  private get getNothingHereLabel() {
    return this.labels["misc.nothingHere"];
  }

  private get getEmptyStampUrl() {
    return getAssetsUrl(emptyStampArt);
  }

  private get getEmptyStampAlt() {
    return `${this.labels["filter.earned"]}`;
  }

  private get getViewOptions(): { value: string; label: string }[] {
    return [
      {
        value: "locked",
        label: `${this.labels["filter.locked"]}`,
      },
      { value: "unlocked", label: `${this.labels["filter.earned"]}` },
    ];
  }

  private get isDefaultVariant(): boolean {
    return this.variant === "default";
  }

  private get inLoadingState() {
    return this.state === "loading";
  }

  private get inLoadedState() {
    return this.state === "loaded";
  }

  private get inEmptyState() {
    return this.state === "empty";
  }

  private get inErrorState() {
    return this.state === "error";
  }

  private get inEmptyStateConfig() {
    return {
      state: "empty",
      empty: {
        title: `${this.labels["filter.earned"]}`,
        description: `${this.labels["misc.nothingHere"]}`,
      },
    };
  }

  private get inErrorStateConfig() {
    return {
      state: this.state,
      error: { refresh: false },
    };
  }

  private get isLoadingMore() {
    return this.loadingMoreStamps;
  }

  private get isSwitchingViews() {
    return this.switchingViews;
  }

  private get getCollectionLayout() {
    return this.switchingViews || this.zeroStamps ? "stack" : "grid";
  }
}
